/**
 * Firebase Helper Utilities
 * Centralized functions for handling Firebase Timestamps, data conversion, and common operations
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Safely converts a Firebase Timestamp or date string to a Date object
 * @param value - Firebase Timestamp, Date, string, or number
 * @returns Date object or null if invalid
 */
export const toDate = (value: any): Date | null => {
  if (!value) return null;
  
  // Handle Firebase Timestamp
  if (value?.toDate && typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch (e) {
      console.error('Error converting Timestamp:', e);
      return null;
    }
  }
  
  // Handle Date object
  if (value instanceof Date) {
    return value;
  }
  
  // Handle string or number
  try {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  } catch (e) {
    console.error('Error parsing date:', e);
    return null;
  }
};

/**
 * Converts a value to ISO string for storage
 * @param value - Any date-like value
 * @returns ISO string or current time if invalid
 */
export const toISOString = (value: any): string => {
  const date = toDate(value);
  return date ? date.toISOString() : new Date().toISOString();
};

/**
 * Formats a date for display
 * @param value - Any date-like value
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string or fallback
 */
export const formatDate = (
  value: any,
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  },
  fallback: string = 'N/A'
): string => {
  const date = toDate(value);
  if (!date) return fallback;
  
  try {
    return date.toLocaleDateString('en-IN', options);
  } catch (e) {
    return fallback;
  }
};

/**
 * Formats a date and time for display
 * @param value - Any date-like value
 * @param fallback - Fallback string
 * @returns Formatted datetime string or fallback
 */
export const formatDateTime = (value: any, fallback: string = 'N/A'): string => {
  return formatDate(
    value,
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    },
    fallback
  );
};

/**
 * Formats time only
 * @param value - Any date-like value
 * @param fallback - Fallback string
 * @returns Formatted time string or fallback
 */
export const formatTime = (value: any, fallback: string = 'N/A'): string => {
  return formatDate(
    value,
    {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    },
    fallback
  );
};

/**
 * Gets timestamp in milliseconds for sorting/comparison
 * @param value - Any date-like value
 * @returns Timestamp in ms or 0 if invalid
 */
export const getTimestamp = (value: any): number => {
  const date = toDate(value);
  return date ? date.getTime() : 0;
};

/**
 * Checks if a date is within the last N hours
 * @param value - Any date-like value
 * @param hours - Number of hours
 * @returns true if within timeframe
 */
export const isRecent = (value: any, hours: number = 24): boolean => {
  const timestamp = getTimestamp(value);
  if (!timestamp) return false;
  
  const now = new Date().getTime();
  const timeframeMs = hours * 60 * 60 * 1000;
  return (now - timestamp) < timeframeMs;
};

/**
 * Safely renders any value as a string (prevents object rendering errors)
 * @param value - Any value
 * @param fallback - Fallback string
 * @returns String representation or fallback
 */
export const safeString = (value: any, fallback: string = 'N/A'): string => {
  if (value === null || value === undefined) return fallback;
  
  // Handle dates and timestamps
  if (value instanceof Date || value?.toDate) {
    return formatDateTime(value, fallback);
  }
  
  // Handle objects (should be avoided in rendering)
  if (typeof value === 'object') {
    console.warn('Attempted to render object as string:', value);
    return fallback;
  }
  
  return String(value);
};

/**
 * Sorts array by date field
 * @param array - Array to sort
 * @param field - Field name containing date
 * @param ascending - Sort direction
 * @returns Sorted array
 */
export const sortByDate = <T extends Record<string, any>>(
  array: T[],
  field: keyof T,
  ascending: boolean = false
): T[] => {
  return [...array].sort((a, b) => {
    const timeA = getTimestamp(a[field]);
    const timeB = getTimestamp(b[field]);
    return ascending ? timeA - timeB : timeB - timeA;
  });
};

/**
 * Deep cleans an object by converting all Timestamps and nested objects
 * @param obj - Object to clean
 * @returns Cleaned object with primitives only
 */
export const deepClean = (obj: any): any => {
  if (obj === null || obj === undefined) return null;
  
  // Handle Timestamp
  if (obj?.toDate && typeof obj.toDate === 'function') {
    return toISOString(obj);
  }
  
  // Handle Date
  if (obj instanceof Date) {
    return obj.toISOString();
  }
  
  // Handle Arrays
  if (Array.isArray(obj)) {
    return obj.map(item => deepClean(item));
  }
  
  // Handle Objects
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        // Skip functions
        if (typeof value === 'function') continue;
        cleaned[key] = deepClean(value);
      }
    }
    return cleaned;
  }
  
  // Return primitives as-is
  return obj;
};

/**
 * Calculates time ago string (e.g., "2 hours ago")
 * @param value - Any date-like value
 * @returns Human-readable time ago string
 */
export const timeAgo = (value: any): string => {
  const timestamp = getTimestamp(value);
  if (!timestamp) return 'Unknown';
  
  const now = new Date().getTime();
  const diff = now - timestamp;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);
  
  if (years > 0) return `${years} year${years > 1 ? 's' : ''} ago`;
  if (months > 0) return `${months} month${months > 1 ? 's' : ''} ago`;
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  return 'Just now';
};

/**
 * Formats currency (Indian Rupees)
 * @param amount - Amount to format
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number | string | undefined | null): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

/**
 * Truncates text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export const truncateText = (text: string | undefined | null, maxLength: number = 50): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};
